import { Check, ShieldAlert } from 'lucide-react';
import { Alert, Button, Spinner } from '@parsina/framework';
import { C, soft } from '@parsina/framework/tokens';

import { useTranslation } from '../../i18n';
import { STACK } from '../../constants/density';

/**
 * «در حال به‌روزرسانی دسترسی شما…» — the token refresh, made visible.
 *
 * Three places in this flow write a Keycloak grant and then have to adopt it
 * before the next request: creating a clinic (the founder's `clinic_admin`
 * composite), seeding roles, and accepting an invitation. In every one of them
 * the session is holding a token issued BEFORE the grant existed, and a token
 * does not retroactively gain role mappings (REV2 §A8).
 *
 * The wizard used to do this as `try { await refreshAccessToken() } catch {}`
 * and walk on. That swallow is why the roles step could open onto a load error
 * with no explanation: the step's own queries are clinic-scoped, the token
 * predates the clinic, and every one of them came back FORBIDDEN. The person
 * saw a broken screen a second after being told their clinic was created.
 *
 * So the refresh is a STEP with two outcomes and a way past both. It never
 * re-runs the mutation that preceded it — the clinic exists, the roles exist,
 * and a retry loop here would be a loop that creates duplicates. `onRetry`
 * retries only the refresh; `onProceed` walks on without it and lets the
 * clinic gate — which has named states for exactly this — explain the rest.
 */
export default function AccessRefreshPanel({
  state,
  successTitle,
  onRetry,
  onProceed,
  proceedLabel,
  busy = false,
  testId = 'access-refresh',
}) {
  const { t } = useTranslation();

  return (
    <div
      data-testid={testId}
      style={{ display: 'flex', flexDirection: 'column', gap: STACK.regular, paddingBlock: 8 }}
    >
      {successTitle ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              width: 28, height: 28, borderRadius: 999, flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: soft(C.green, 0.14), color: C.green,
            }}
          >
            <Check size={16} strokeWidth={3} />
          </span>
          <div style={{ fontSize: '0.875rem', fontWeight: 800, color: C.textPrimary, minWidth: 0 }}>
            {successTitle}
          </div>
        </div>
      ) : null}

      {state === 'refreshing' ? (
        <div
          aria-live="polite"
          style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.8125rem', color: C.textSecondary }}
        >
          <Spinner size={16} />
          <span>{t('auth.createClinic.refreshing')}</span>
        </div>
      ) : null}

      {state === 'failed' ? (
        <>
          <Alert
            variant="error"
            role="alert"
            icon={<ShieldAlert size={16} aria-hidden="true" />}
          >
            {t('auth.createClinic.refreshFailed')}
          </Alert>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button onClick={onRetry} disabled={busy} aria-busy={busy || undefined}>
              {busy ? <Spinner size={14} /> : null}
              {busy ? t('common.state.saving') : t('common.actions.retry')}
            </Button>
            {onProceed ? (
              <Button variant="outline" onClick={onProceed} disabled={busy}>
                {proceedLabel || t('auth.createClinic.enterWithoutRoles')}
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
